import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Videocam as WebcamIcon,
  CameraAlt as CaptureIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ZoomIn as ZoomInIcon,
  RotateRight as RotateIcon,
} from '@mui/icons-material';
import Cropper, { Area } from 'react-easy-crop';
import PhotoCaptureModalEnhanced from './PhotoCaptureModalEnhanced';
import { detectFace, FaceDetectionResult } from '../utils/faceDetection';
import { getCroppedImg, createImage, padImageWithBlack, dataUrlToPhotoOriginal } from '../utils/imageUtils';
import { useWebcam } from '../hooks/useWebcam';
import { PhotoHelpTooltip } from './PhotoHelpTooltip';
import { PhotoGuidelinesModal, shouldShowPhotoGuidelines } from './PhotoGuidelinesModal';
import { useOperationalSettings } from '../contexts/OperationalSettingsContext';
import type { PhotoResult } from '../types/photo';

const PADDING_RATIO = 0.5;
const ZOOM_FOR_ORIGINAL_FILL = 1 + 2 * PADDING_RATIO; // 2.0
const CROPPER_MAX_ZOOM = 8;

// Re-exported from the one shared definition (src/types/photo.ts).
export type { PhotoResult };

interface PhotoWebcamModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PhotoResult) => void;
  title?: string;
  cropShape?: 'round' | 'rect';
  aspectRatio?: number;
  darkMode?: boolean;
  disableFaceDetection?: boolean; // Skip the facial-ID guidelines screen (e.g. for non-face photos)
}

const PhotoWebcamModal: React.FC<PhotoWebcamModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Captura via Webcam',
  cropShape = 'rect',
  aspectRatio = 4 / 5,
  darkMode = false,
  disableFaceDetection = false,
}) => {
  const { settings } = useOperationalSettings();

  // Use the webcam hook
  const {
    videoRef,
    isActive,
    isLoading,
    isReady,
    error: webcamError,
    start,
    stop,
    capture,
    availableCameras,
    selectedCamera,
    selectCamera,
  } = useWebcam();

  // Image processing state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [isDetectingFace, setIsDetectingFace] = useState(false);
  const [faceDetectionResult, setFaceDetectionResult] = useState<FaceDetectionResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [bgColor, setBgColor] = useState<'#000000' | '#ffffff'>('#000000');
  const origDataUrlRef = useRef<string | null>(null);
  const hasStartedCamera = useRef(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Capture from webcam
  const captureFromWebcam = useCallback(async () => {
    if (!isReady) return;

    const blob = await capture();
    if (!blob) {
      setError('Erro ao capturar imagem da webcam');
      return;
    }

    // Convert blob to data URL
    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result as string;
      stop();
      await processImage(dataUrl);
    };
    reader.readAsDataURL(blob);
  }, [isReady, capture, stop]);

  // Process captured image
  const processImage = useCallback(async (dataUrl: string) => {
    setError(null);
    setIsDetectingFace(false);

    try {
      origDataUrlRef.current = dataUrl;
      const origImg = await createImage(dataUrl);
      const paddedDataUrl = await padImageWithBlack(dataUrl, PADDING_RATIO, bgColor);
      setImageSrc(paddedDataUrl);

      if (settings.autoFaceDetection) {
        setIsDetectingFace(true);
        const detection = await detectFace(origImg);
        setFaceDetectionResult(detection);
        setIsDetectingFace(false);
      } else {
        setFaceDetectionResult(null);
      }

      // Always center regardless of face detection
      setCrop({ x: 0, y: 0 });
      setZoom(ZOOM_FOR_ORIGINAL_FILL);
      setRotation(0);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Erro ao processar a imagem. Tente novamente.');
      setIsDetectingFace(false);
    }
  }, [settings.autoFaceDetection, bgColor]);

  // Re-pad image when background color changes
  useEffect(() => {
    if (!origDataUrlRef.current || !imageSrc) return;
    const rePad = async () => {
      const paddedDataUrl = await padImageWithBlack(origDataUrlRef.current!, PADDING_RATIO, bgColor);
      setImageSrc(paddedDataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(ZOOM_FOR_ORIGINAL_FILL);
    };
    rePad();
  }, [bgColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate preview when crop changes
  useEffect(() => {
    const generatePreview = async () => {
      if (!imageSrc || !croppedAreaPixels) return;

      try {
        const ph = 150;
        const pw = cropShape === 'round' ? ph : Math.round(ph * aspectRatio);
        const result = await getCroppedImg(
          imageSrc,
          croppedAreaPixels,
          rotation,
          { width: pw, height: ph }
        );
        setCroppedPreview(result.base64);
      } catch (err) {
        console.error('Error generating preview:', err);
      }
    };

    const timeoutId = setTimeout(generatePreview, 100);
    return () => clearTimeout(timeoutId);
  }, [imageSrc, croppedAreaPixels, rotation]);

  // Auto-show guidelines on first open
  useEffect(() => {
    if (open && !imageSrc && !disableFaceDetection && shouldShowPhotoGuidelines()) {
      // Show guidelines first before starting camera (only if user hasn't disabled it)
      setShowGuidelines(true);
      hasStartedCamera.current = false;
    }
  }, [open, imageSrc, disableFaceDetection]);

  // Start webcam when guidelines are closed (only once)
  useEffect(() => {
    if (open && !imageSrc && !showGuidelines && !hasStartedCamera.current) {
      console.log(' Starting webcam...');
      hasStartedCamera.current = true;
      // Start after guidelines are dismissed
      start();
    }
  }, [open, imageSrc, showGuidelines]);

  // Separate timeout effect - only runs when actively loading
  useEffect(() => {
    if (open && !imageSrc && !showGuidelines && isLoading && !isReady) {
      console.log(' Starting camera timeout watch...');
      // Force timeout after 10 seconds if still loading
      const timeoutId = setTimeout(() => {
        console.error(' Camera startup timeout - forcing error');
        setError('A câmera não respondeu. Por favor, feche este modal e use "Enviar Foto do Dispositivo".');
        stop();
      }, 10000);

      return () => {
        console.log(' Clearing camera timeout');
        clearTimeout(timeoutId);
      };
    }
  }, [open, imageSrc, showGuidelines, isLoading, isReady]);

  // Reset state and stop webcam when modal closes
  useEffect(() => {
    if (!open) {
      // CRITICAL: Reset focus to prevent keyboard trap on Windows
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Small delay to ensure focus is properly released
      setTimeout(() => {
        if (document.body) {
          document.body.focus();
        }
      }, 50);

      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setCroppedPreview(null);
      setFaceDetectionResult(null);
      setError(null);
      stop();
      hasStartedCamera.current = false;
    }
  }, [open, stop]);

  // Handle camera change
  const handleCameraChange = useCallback(async (deviceId: string) => {
    await selectCamera(deviceId);
    // Restart webcam with new camera
    setTimeout(() => start(), 150);
  }, [selectCamera, start]);

  // Retake photo
  const handleRetake = useCallback(() => {
    setImageSrc(null);
    setCroppedPreview(null);
    setFaceDetectionResult(null);
    start();
  }, [start]);

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
        { width: 800, height: 800 },
        { horizontal: false, vertical: false },
        bgColor
      );

      onConfirm({
        blob: result.blob,
        base64: result.base64,
        width: result.width,
        height: result.height,
        faceDetected: faceDetectionResult?.detected || false,
        faceConfidence: faceDetectionResult?.confidence,
        // The full captured frame, before padding and cropping.
        original: dataUrlToPhotoOriginal(origDataUrlRef.current),
      });
    } catch (err) {
      console.error('Error processing final image:', err);
      setError('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  }, [imageSrc, croppedAreaPixels, rotation, faceDetectionResult, onConfirm, bgColor]);

  const displayError = error || webcamError;

  // Capture stays here; EDITING is the one shared editor.
  //
  // This modal used to carry its own cropper, so which editor an operator got depended on how the
  // photo was taken — and fixes made in one never reached the other. Delegating the whole modal
  // was tried and was wrong: the capture step (device enumeration, the live preview, the
  // guidelines) belongs to the host. Only the frame crosses over.
  if (imageSrc) {
    return (
      <PhotoCaptureModalEnhanced
        open={open}
        onClose={onClose}
        onConfirm={onConfirm}
        existingPhoto={imageSrc}
        editOnly
        onRetake={handleRetake}
        retakeLabel="Tirar outra foto"
        captureSource="webcam"
        title={title}
        cropShape={cropShape}
        aspectRatio={aspectRatio}
        disableFaceDetection={disableFaceDetection}
        darkMode={darkMode}
        hideIPCamera
      />
    );
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={(typeof window !== 'undefined' && window.innerWidth < 768)} // Fullscreen on mobile
      TransitionProps={{
        timeout: {
          enter: 150, // Faster opening (default is 225ms)
          exit: 150,  // Faster closing
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 }, // No border radius on mobile
          bgcolor: darkMode ? '#1a1a1a' : '#fff',
          color: darkMode ? '#fff' : '#000',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          bgcolor: darkMode ? '#212121' : '#f5f5f5',
          borderBottom: '1px solid',
          borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ color: darkMode ? 'grey.100' : 'grey.900', textTransform: 'uppercase', letterSpacing: 1 }}>{title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhotoHelpTooltip onOpenFullManual={() => setShowGuidelines(true)} darkMode={darkMode} />
          <IconButton onClick={onClose} size="small" sx={{ color: darkMode ? 'grey.400' : 'grey.600', '&:hover': { color: darkMode ? 'grey.100' : 'grey.900' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          bgcolor: darkMode ? '#1a1a1a' : '#fff',
          borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        }}
      >
        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            <Typography variant="body2" gutterBottom>{displayError}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Dica: Se a câmera não funcionar, feche este modal e use a opção "Enviar Foto do Dispositivo".
            </Typography>
          </Alert>
        )}

        {!imageSrc ? (
          // Webcam view
          <Box>
            {/* Camera selector */}
            {availableCameras.length > 1 && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Câmera</InputLabel>
                <Select
                  value={selectedCamera || ''}
                  label="Câmera"
                  onChange={(e) => handleCameraChange(e.target.value)}
                >
                  {availableCameras.map((camera, index) => (
                    <MenuItem key={camera.deviceId} value={camera.deviceId}>
                      {camera.label || `Câmera ${index + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Video preview */}
            <Box
              sx={{
                position: 'relative',
                height: { xs: 300, sm: 400 }, // Smaller on mobile
                bgcolor: 'grey.900',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isLoading && (
                <Box sx={{ position: 'absolute', textAlign: 'center', zIndex: 10 }}>
                  <CircularProgress sx={{ color: 'white' }} />
                  <Typography sx={{ mt: 2, color: 'white' }}>Iniciando câmera...</Typography>
                </Box>
              )}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'scaleX(-1)', // Mirror effect
                  opacity: isReady ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              />
            </Box>

            {/* Status indicator */}
            {isReady && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Câmera pronta
              </Typography>
            )}

            {/* Capture button */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CaptureIcon />}
                onClick={captureFromWebcam}
                disabled={!isReady || isLoading}
                sx={{ minWidth: 200 }}
              >
                Capturar Foto
              </Button>
            </Box>
          </Box>
        ) : (
          // Cropper area
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Left: Cropper */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ position: 'relative', height: 350, bgcolor: 'grey.900', borderRadius: 1, overflow: 'hidden' }}>
                {isDetectingFace ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2, color: 'white' }}>Detectando rosto...</Typography>
                  </Box>
                ) : (
                  <Cropper
                    key={imageSrc} // Force remount when image changes to prevent DOM errors
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={aspectRatio}
                    cropShape={cropShape}
                    showGrid={cropShape === 'rect'}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    // The library default of 1 makes the wheel crawl: a whole photo down to a
                    // number plate is one long unbroken scroll.
                    zoomSpeed={2.5}
                    minZoom={0.1}
                    maxZoom={CROPPER_MAX_ZOOM}
                    restrictPosition={false}
                  />
                )}
              </Box>

              {/* Controls */}
              <Box sx={{ mt: 2, px: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <ZoomInIcon fontSize="small" sx={{ color: darkMode ? 'grey.400' : 'grey.600' }} />
                  <Slider
                    value={zoom}
                    min={0.1}
                    max={CROPPER_MAX_ZOOM}
                    step={0.1}
                    onChange={(_, value) => setZoom(value as number)}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <RotateIcon fontSize="small" sx={{ color: darkMode ? 'grey.400' : 'grey.600' }} />
                  <Slider
                    value={rotation}
                    min={0}
                    max={360}
                    onChange={(_, value) => setRotation(value as number)}
                    sx={{ flex: 1 }}
                  />
                </Box>
                {/* Background color toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: darkMode ? 'grey.500' : 'grey.600' }}>Fundo:</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setBgColor('#000000')}
                    title="Fundo preto"
                    sx={{
                      width: 22, height: 22, bgcolor: '#000000', borderRadius: 0.5,
                      border: bgColor === '#000000' ? '2px solid #1976d2' : '2px solid #999',
                      '&:hover': { bgcolor: '#222' },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setBgColor('#ffffff')}
                    title="Fundo branco"
                    sx={{
                      width: 22, height: 22, bgcolor: '#ffffff', borderRadius: 0.5,
                      border: bgColor === '#ffffff' ? '2px solid #1976d2' : '2px solid #999',
                      '&:hover': { bgcolor: '#eee' },
                    }}
                  />
                </Box>
              </Box>

              {/* Retake button */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<CaptureIcon />}
                  onClick={handleRetake}
                >
                  Tirar outra foto
                </Button>
              </Box>
            </Box>

            {/* Right: Preview */}
            <Box sx={{ width: 160, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                Prévia
              </Typography>
              <Box
                sx={{
                  width: cropShape === 'round' ? 150 : Math.round(150 * aspectRatio),
                  height: 150,
                  borderRadius: cropShape === 'round' ? '50%' : 1.5,
                  overflow: 'hidden',
                  border: '3px solid',
                  borderColor: faceDetectionResult?.detected ? 'success.main' : 'grey.300',
                  bgcolor: darkMode ? 'grey.800' : 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {croppedPreview ? (
                  <img
                    src={croppedPreview}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ color: darkMode ? 'grey.600' : 'grey.400', textAlign: 'center', px: 1 }}>
                    Preview here..
                  </Typography>
                )}
              </Box>

              {/* Face detection status */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                {faceDetectionResult?.detected ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                    <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">Rosto detectado</Typography>
                  </Box>
                ) : faceDetectionResult && !faceDetectionResult.detected ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                    <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">Rosto não detectado</Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: darkMode ? '#212121' : '#f5f5f5',
          borderTop: '1px solid',
          borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        }}
      >
        <Button onClick={onClose} sx={{ color: darkMode ? 'grey.300' : 'grey.700', '&:hover': { bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' } }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!imageSrc || !croppedAreaPixels || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {isProcessing ? 'Processando...' : 'Confirmar'}
        </Button>
      </DialogActions>

      {/* Photo Guidelines Modal */}
      <PhotoGuidelinesModal
        open={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        darkMode={darkMode}
      />
    </Dialog>
  );
};

export default PhotoWebcamModal;
